<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useAiChatStore } from '@/stores/ai-chat'
import type { AskUserAnswer, AskUserQuestion } from '@/api/ai'
import { Button } from '@kyoto2/components/ui/button'
import { Input } from '@kyoto2/components/ui/input'
import { Loader2 } from 'lucide-vue-next'

const props = defineProps<{ routeContext?: string }>()
const store = useAiChatStore()
const submitting = ref(false)

/** Per-question local state keyed by question id. */
const selections = reactive<Record<string, string[]>>({})
const customTexts = reactive<Record<string, string>>({})

const questions = computed(() => store.pendingUserQuestions?.questions ?? [])

watch(
  () => store.pendingUserQuestions?.tool_use_id,
  () => {
    for (const key of Object.keys(selections)) delete selections[key]
    for (const key of Object.keys(customTexts)) delete customTexts[key]
    for (const q of questions.value) {
      selections[q.id] = []
      customTexts[q.id] = ''
    }
  },
  { immediate: true },
)

function ensureState(q: AskUserQuestion) {
  if (!selections[q.id]) selections[q.id] = []
  if (customTexts[q.id] === undefined) customTexts[q.id] = ''
}

function isSelected(q: AskUserQuestion, optionId: string): boolean {
  ensureState(q)
  return selections[q.id].includes(optionId)
}

function toggleOption(q: AskUserQuestion, optionId: string) {
  ensureState(q)
  const multi = q.selection === 'multiple'
  if (multi) {
    const idx = selections[q.id].indexOf(optionId)
    if (idx >= 0) selections[q.id].splice(idx, 1)
    else selections[q.id].push(optionId)
  } else {
    selections[q.id] = [optionId]
  }
}

function isQuestionAnswered(q: AskUserQuestion): boolean {
  ensureState(q)
  const hasSelection = selections[q.id].length > 0
  const custom = customTexts[q.id]?.trim()
  if (q.allow_custom) return hasSelection || !!custom
  return hasSelection
}

const allAnswered = computed(() => questions.value.every(isQuestionAnswered))

function buildAnswers(): AskUserAnswer[] {
  return questions.value.map(q => {
    ensureState(q)
    const custom = customTexts[q.id]?.trim()
    return {
      question_id: q.id,
      selected_option_ids: [...selections[q.id]],
      ...(custom && q.allow_custom ? { custom_text: custom } : {}),
    }
  })
}

async function submit() {
  if (store.isStreaming || !allAnswered.value) return
  submitting.value = true
  try {
    await store.resumeWithAnswers(buildAnswers(), props.routeContext)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div
    v-if="store.pendingUserQuestions"
    class="mx-2.5 mb-2 rounded-lg border border-border bg-muted/40 p-3 space-y-3 min-w-0"
  >
    <div class="space-y-1 min-w-0">
      <p class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Needs your input
      </p>
      <p
        v-if="store.pendingUserQuestions.title"
        class="text-sm font-medium leading-snug text-foreground break-words"
      >
        {{ store.pendingUserQuestions.title }}
      </p>
    </div>

    <div
      v-for="q in questions"
      :key="q.id"
      class="space-y-2 min-w-0"
    >
      <p class="text-sm font-medium leading-snug text-foreground break-words">
        {{ q.prompt }}
      </p>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-for="opt in q.options"
          :key="opt.id"
          size="sm"
          :variant="isSelected(q, opt.id) ? 'default' : 'outline'"
          class="h-8"
          :disabled="store.isStreaming || submitting"
          :title="opt.description"
          @click="toggleOption(q, opt.id)"
        >
          {{ opt.label }}
        </Button>
      </div>
      <Input
        v-if="q.allow_custom"
        v-model="customTexts[q.id]"
        class="h-8"
        placeholder="Or type your own…"
        :disabled="store.isStreaming || submitting"
      />
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        class="h-8"
        :disabled="store.isStreaming || submitting || !allAnswered"
        @click="submit"
      >
        <Loader2 v-if="submitting" class="size-3.5 mr-1 animate-spin" />
        Submit
      </Button>
    </div>
  </div>
</template>
